import {
  differenceInMinutes,
  isBefore,
  addMinutes,
  differenceInMonths,
  differenceInWeeks,
  differenceInCalendarDays,
} from 'date-fns';
import { HourlyData } from 'types/models/ForecastDto';
import { Weather } from '../../../types/models/Weather';
import { MeasurementData } from '../../weather/types';
import { convertNumberFromDB } from '../../weather/utils/dataConverters';

export const convertWeatherData = (
  weatherData: (Weather | HourlyData)[],
  measurement: MeasurementData
) => {
  const convertedData: {
    x: Date;
    y: number | null;
    wind_dir: string | null | undefined;
  }[] = [];
  let previousDatum: Weather | HourlyData | null = null;
  for (const weatherDatum of weatherData) {
    if (
      previousDatum &&
      Math.abs(
        differenceInMinutes(
          new Date(weatherDatum.time),
          new Date(previousDatum.time)
        )
      ) > 30
    ) {
      let dateForNullEntry = new Date(previousDatum.time);
      while (isBefore(dateForNullEntry, new Date(weatherDatum.time))) {
        convertedData.push({
          x: dateForNullEntry,
          y: null,
          wind_dir: null,
        });
        dateForNullEntry = addMinutes(dateForNullEntry, 15);
      }
    }
    convertedData.push(convertWeatherDatum(weatherDatum, measurement));
    previousDatum = weatherDatum;
  }
  return convertedData;
};

export const convertSecondaryWeatherData = (
  weatherData: (Weather | HourlyData)[],
  measurement: MeasurementData
) => {
  if (measurement === 'wind_kph')
    return convertWeatherData(weatherData, 'gust_kph');
  if (measurement === 'gust_kph')
    return convertWeatherData(weatherData, 'wind_kph');
  if (measurement === 'temp_c')
    return convertWeatherData(weatherData, 'dewpoint_c');
  if (measurement === 'dewpoint_c')
    return convertWeatherData(weatherData, 'temp_c');
};

export const isThereSecondaryData = (measurement: MeasurementData) => {
  if (
    measurement === 'wind_kph' ||
    measurement === 'gust_kph' ||
    measurement === 'temp_c' ||
    measurement === 'dewpoint_c'
  )
    return true;
  return false;
};

export const getSecondaryDataType = (measurement: MeasurementData) => {
  if (measurement === 'wind_kph') return 'gust_kph';
  if (measurement === 'gust_kph') return 'wind_kph';
  if (measurement === 'temp_c') return 'dewpoint_c';
  if (measurement === 'dewpoint_c') return 'temp_c';
  return '';
};

export const convertWeatherDatum = (
  weatherDatum: Weather | HourlyData,
  measurement: MeasurementData
) => {
  return {
    x: new Date(weatherDatum.time),
    y: convertNumberFromDB(weatherDatum[measurement], measurement),
    wind_dir:
      measurement === 'wind_kph' || measurement === 'gust_kph'
        ? weatherDatum.wind_dir
        : null,
  };
};

export const getMinMaxMean = (
  weatherData: (Weather | HourlyData)[] | undefined,
  measurement: MeasurementData | undefined | ''
) => {
  if (!weatherData || !measurement) {
    return { min: 'NA', max: 'NA', mean: 'NA' };
  }
  let min = Number.MAX_VALUE;
  let max = -Number.MAX_VALUE;
  let sum = 0;
  let count = 0;
  for (const weatherDatum of weatherData) {
    const currentDataPoint = weatherDatum[measurement];
    if (currentDataPoint === undefined || currentDataPoint === null) continue;
    if (currentDataPoint < min) min = currentDataPoint;
    if (currentDataPoint > max) max = currentDataPoint;
    sum += currentDataPoint;
    count += 1;
  }
  return {
    min: convertNumberFromDB(min, measurement)?.toFixed(2),
    max: convertNumberFromDB(max, measurement)?.toFixed(2),
    mean: convertNumberFromDB(sum / count, measurement)?.toFixed(2),
  };
};

export const getTimeUnitForYAxis = (weatherData: (Weather | HourlyData)[]) => {
  if (weatherData.length === 0) return;
  const startTime = new Date(weatherData[0].time);
  const endTime = new Date(weatherData[weatherData.length - 1].time);
  if (differenceInMonths(endTime, startTime) > 1) return 'month';
  if (differenceInWeeks(endTime, startTime) > 1) return 'week';
  if (differenceInCalendarDays(endTime, startTime) > 1) return 'day';
};

// The function expects a label of the format, e.g., (1 days)
export const formatSingularPluralRangeLabels = (label: string) => {
  if (label[1] === '1' && label[2] === '')
    return label.slice(0, label.length - 2) + ')';
  return label;
};
